import getAnnotations from './query/getAnnotations.gql';
import getAnnotation from './query/getAnnotation.gql';
import createAnnotation from './query/createAnnotation.gql';
import updateAnnotation from './query/updateAnnotation.gql';
import client from '../../ConnectionManager/apolloClient';
import CoursewareItemProvider from '../coursewareItem/CoursewareItemProvider';
import AnnotationsCache from './AnnotationsByCoursewareCache';
import {
  validateMotivation,
  validateBodyString,
  createAnnotationBodyString,
  createAnnotationTargetString,
  sortByModifiedAndCreated,
  clientAnnotationToMercuryAnnotation,
  mercuryAnnotationToClientAnnotation,
} from './helpers';

class AnnotationsProvider {
  constructor() {
    this._annotationsCache = {};
  }

  createCache(motivation) {
    if (!this._annotationsCache[motivation]) {
      this._annotationsCache[motivation] = new AnnotationsCache(motivation);
    }
  }

  async initializeMotivationCache(motivation) {
    this.createCache(motivation);

    if (!this._annotationsCache[motivation].initialized) {
      await this._fetchAnnotationsByMotivation(motivation);
    }
  }

  async getAnnotationsByMotivation(motivation, coursewareId) {
    validateMotivation(motivation);
    await this.initializeMotivationCache(motivation);
    const annotations = this._annotationsCache[motivation].getAnnotationsByCoursewareId(coursewareId);
    annotations.sort(sortByModifiedAndCreated);
    return annotations;
  }

  async getAnnotationById(motivation, annotationId) {
    this.createCache(motivation);

    let annotation;
    if (this._annotationsCache[motivation].initialized) {
      annotation = this._annotationsCache[motivation].getAnnotation(annotationId);
    }

    if (!annotation) {
      annotation = await this._fetchAnnotationById(annotationId);
      this._annotationsCache[motivation].saveAnnotation(annotation);
    }

    return annotation;
  }

  /**
   * Fetch annotation by given id from mercury
   */
  async _fetchAnnotationById(annotationId) {
    const { deploymentId } = CoursewareItemProvider.getDeploymentContext();
    const { data } = await client.query({
      query: getAnnotation,
      variables: {
        annotationId,
        deploymentId,
      },
      errorPolicy: 'ignore',
      fetchPolicy: 'network-only',
    });

    const annotation = data.AnnotationById
      ? await mercuryAnnotationToClientAnnotation(data.AnnotationById)
      : {};

    return annotation;
  }

  async subscribeToAnnotationMotivation(motivation, coursewareId, callback) {
    validateMotivation(motivation);
    // Don't need this to be synchronous just need to ensure we're asking for the data
    await this.initializeMotivationCache(motivation);
    return this._annotationsCache[motivation].subscribe(coursewareId, callback);
  }

  /**
   * Fetch all annotations from mercury
   */
  async _fetchAnnotationsByMotivation(motivation) {
    const { deploymentId } = CoursewareItemProvider.getDeploymentContext();
    const { data } = await client.query({
      query: getAnnotations,
      variables: {
        motivation,
        deploymentId,
      },
      errorPolicy: 'ignore',
      fetchPolicy: 'network-only',
    });
    const annotations = data.AnnotationsByDeployment
      ? await Promise.all(data.AnnotationsByDeployment.map(mercuryAnnotationToClientAnnotation))
      : [];
    this._annotationsCache[motivation].saveAnnotations(annotations);
  }

  /**
   * Change annotation in mercury
   */
  async setAnnotationBody(annotationId, body) {
    const annotation = this._findAnnotation(annotationId);
    if (!annotation) {
      throw Error(`Couldn't find annotation ${annotationId} in local cache`);
    }

    const mercuryAnnotation = clientAnnotationToMercuryAnnotation({
      ...annotation,
      body,
    });

    const { data: { AnnotationForDeploymentUpdate } } = await client.mutate({
      mutation: updateAnnotation,
      variables: {
        id: mercuryAnnotation.id,
        body: mercuryAnnotation.body,
        target: mercuryAnnotation.target,
      },
      errorPolicy: 'ignore',
    });

    const clientAnnotation = await mercuryAnnotationToClientAnnotation(AnnotationForDeploymentUpdate);
    this._annotationsCache[clientAnnotation.motivation].saveAnnotation(clientAnnotation);
  }

  /**
   * Create annotation in mercury
   */
  async createAnnotation(motivation, body, target, subTarget) {
    validateMotivation(motivation);

    const bodyString = createAnnotationBodyString(body);
    validateBodyString(bodyString);

    const targetString = createAnnotationTargetString(target, subTarget);
    const { data: { AnnotationForDeploymentCreate } } = await client.mutate({
      mutation: createAnnotation,
      variables: {
        motivation,
        deploymentId: target.deploymentId,
        elementId: target.id,
        body: bodyString,
        target: targetString,
      },
      errorPolicy: 'ignore',
    });

    const clientAnnotation = await mercuryAnnotationToClientAnnotation(AnnotationForDeploymentCreate);
    this._annotationsCache[clientAnnotation.motivation].saveAnnotation(clientAnnotation);

    return clientAnnotation;
  }

  _findAnnotation(annotationId) {
    return Object.values(this._annotationsCache)
      .find((cache) => cache.getAnnotation(annotationId));
  }
}

export default new AnnotationsProvider();
